import { useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { captureSentryException } from '../lib/sentry';
import {
  VOICE_RECORDING_OPTIONS,
  activateVoiceSession,
  deactivateVoiceSession,
  normalizeMetering,
  reassertVoiceSession,
  requestMicrophonePermission,
  useAudioRecorder,
  useAudioRecorderState,
  type MicrophonePermissionStatus,
} from '../lib/voice/audio';
import { discardVoiceFile } from '../lib/voice/voice-files';

export type VoiceRecordingResult =
  | {
      ok: true;
      uri: string;
      durationMs: number;
    }
  | {
      ok: false;
      reason: 'no-recording' | 'permission' | 'error';
      permissionStatus?: MicrophonePermissionStatus;
      error?: unknown;
    };

export type VoiceRecorderStartResult =
  | { ok: true }
  | { ok: false; reason: 'permission'; permissionStatus: MicrophonePermissionStatus }
  | { ok: false; reason: 'error'; error?: unknown };

export type VoiceRecorder = {
  start: () => Promise<VoiceRecorderStartResult>;
  stop: () => Promise<VoiceRecordingResult>;
  cancel: () => Promise<void>;
  /**
   * Stops the native recorder whatever this hook believes: one the system
   * restarted by itself (iOS resumes paused and prepared recorders). Deletes
   * the file with `freshFilePerTake`.
   */
  forceStop: () => Promise<void>;
  isRecording: boolean;
  durationMs: number;
  // 0..1: normalized mic input level for waveforms / glows.
  level: number;
  /**
   * Raw metering in dBFS, or undefined when the platform reports none. `level`
   * reads 0 for both silence and missing metering; web Live mode's turn
   * detector needs to tell them apart.
   */
  metering: number | undefined;
};

export interface VoiceRecorderOptions {
  /**
   * Give every take its own file (native). Without it iOS records each take
   * into the same file, so a take started while the last one uploads
   * overwrites it. With it the recorder also deletes its file on cancel; the
   * caller owns (and deletes) a stopped take.
   */
  freshFilePerTake?: boolean;
}

// Foundation hook for SCRUM-99. Wraps expo-audio's recorder + audio session
// + permission flow. Consumers (SCRUM-91 push-to-talk, SCRUM-101 hands-free)
// call start/stop and read `level` for visualisations.
export function useVoiceRecorder({ freshFilePerTake = false }: VoiceRecorderOptions = {}): VoiceRecorder {
  const recorder = useAudioRecorder(VOICE_RECORDING_OPTIONS);
  // 60ms, not the default 100ms: the listening face eases each sample over
  // 110ms, so a slower poll than this shows up as a staircase rather than a
  // voice. Each tick re-renders the consumer, which is why it is not lower.
  const state = useAudioRecorderState(recorder, 60);
  const recordingRef = useRef(false);
  const ownsFiles = freshFilePerTake && Platform.OS !== 'web';
  /**
   * The open take's file, kept in JS: on unmount Expo releases the native
   * recorder before its owner's cleanup runs, after which `recorder.uri`
   * can't be read to delete it. Ownership passes to the caller on stop().
   */
  const ownedUriRef = useRef<string | null>(null);

  /** Frees the open take's file (cancelled, or a recorder the system restarted). */
  const discardOwned = useCallback(() => {
    const uri = ownedUriRef.current;
    ownedUriRef.current = null;
    if (ownsFiles) discardVoiceFile(uri);
  }, [ownsFiles]);

  const start = useCallback(async (): Promise<VoiceRecorderStartResult> => {
    const permission = await requestMicrophonePermission();
    if (permission !== 'granted') {
      return { ok: false as const, reason: 'permission' as const, permissionStatus: permission };
    }
    await activateVoiceSession();
    // iOS creates a new recorder (and file) only when it is given options.
    const prepare = () =>
      ownsFiles ? recorder.prepareToRecordAsync(VOICE_RECORDING_OPTIONS) : recorder.prepareToRecordAsync();
    try {
      try {
        await prepare();
      } catch {
        // iOS intermittently fails to prepare when the audio session is
        // contended (another app / a just-finished playback still holds it).
        // Cycling the session and re-preparing once recovers most of these.
        await deactivateVoiceSession();
        await new Promise((resolve) => setTimeout(resolve, 300));
        // Applied again even when a Live conversation holds the session (and
        // the deactivate above did nothing).
        await reassertVoiceSession();
        await prepare();
      }
      recorder.record();
      if (ownsFiles) ownedUriRef.current = recorder.uri ?? null;
    } catch (error) {
      try {
        // Leaves audio session in 'recording' phase otherwise: mic LED stays on.
        await deactivateVoiceSession();
      } catch {
        // Best-effort: matches stop()/cancel(). Not letting teardown failure swallow telemetry.
      }
      captureSentryException(error, {
        scope: 'voice_recorder_start',
        permissionStatus: permission,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      return { ok: false as const, reason: 'error' as const, error };
    }
    recordingRef.current = true;
    return { ok: true as const };
  }, [ownsFiles, recorder]);

  const stop = useCallback(async (): Promise<VoiceRecordingResult> => {
    if (!recordingRef.current) {
      return { ok: false, reason: 'no-recording' };
    }
    try {
      // Read duration before stop: recorder.currentTime resets on stop on some platforms.
      const durationMs = Math.round(recorder.currentTime * 1000);
      await recorder.stop();
      recordingRef.current = false;
      const uri = recorder.uri;
      // The caller owns a stopped take (it uploads, then deletes it).
      ownedUriRef.current = null;
      await deactivateVoiceSession();
      if (!uri) {
        return { ok: false, reason: 'error' };
      }
      return { ok: true, uri, durationMs };
    } catch (error) {
      recordingRef.current = false;
      await deactivateVoiceSession();
      return { ok: false, reason: 'error', error };
    }
  }, [recorder]);

  const cancel = useCallback(async () => {
    if (!recordingRef.current) return;
    try {
      await recorder.stop();
    } catch {
      // Swallow: cancellation is best-effort.
    }
    recordingRef.current = false;
    // Nobody will upload a cancelled take.
    discardOwned();
    await deactivateVoiceSession();
  }, [discardOwned, recorder]);

  const forceStop = useCallback(async () => {
    // Only a recorder really recording now (a live native read, not the poll):
    // a take just stopped and on its way to the server must never be touched.
    let recording = false;
    try {
      recording = recorder.isRecording;
    } catch {
      // Released: nothing is recording.
    }
    if (!recording) return;
    try {
      await recorder.stop();
    } catch {
      // Already stopped.
    }
    recordingRef.current = false;
    if (!ownedUriRef.current && ownsFiles) {
      try {
        ownedUriRef.current = recorder.uri ?? null;
      } catch {
        // Released already: nothing more to find.
      }
    }
    discardOwned();
  }, [discardOwned, ownsFiles, recorder]);

  return {
    start,
    stop,
    cancel,
    forceStop,
    isRecording: state.isRecording,
    durationMs: state.durationMillis,
    level: normalizeMetering(state.metering),
    metering: state.metering,
  };
}
